import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Building2, Loader2, RefreshCw, Utensils, CheckCircle2, XCircle, Search } from 'lucide-react';
import api from '../services/api';

interface TxRow {
  transactionID?: number;
  employeeID?: number;
  employeeName?: string;
  employeeCode?: string;
  cardNumber?: string;
  departmentName?: string;
  periodID?: number | null;
  periodName?: string;
  deviceName?: string;
  transactionDate?: string;
  transactionTime?: string;
  isAuthorized?: boolean;
  denyReason?: string | null;
}
interface Period { periodID: number; periodName: string; }
interface Department { departmentId: number; id?: number; departmentName?: string; name?: string; }

const todayStr = () => new Date().toISOString().substring(0, 10);
const daysAgoStr = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().substring(0, 10); };

const fmtTime = (t?: string) => {
  if (!t) return '—';
  const m = String(t).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(t).substring(0, 5);
};
const fmtDate = (d?: string) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d).substring(0, 10) : dt.toLocaleDateString('tr-TR');
};

const MealTransactions: React.FC = () => {
  const [start, setStart] = useState(daysAgoStr(7));
  const [end, setEnd] = useState(todayStr());
  const [periodId, setPeriodId] = useState<number | ''>('');
  const [depId, setDepId] = useState<number | ''>('');
  const [authorized, setAuthorized] = useState<'' | 'true' | 'false'>('');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState<TxRow[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    try {
      const rP: any = await api.get('/meals/periods');
      const rawP = rP?.data ?? rP;
      setPeriods(Array.isArray(rawP) ? rawP : (Array.isArray(rawP?.data) ? rawP.data : []));
    } catch { setPeriods([]); }
    try {
      const rD: any = await api.get('/departments');
      const rawD = rD?.data ?? rD;
      setDepartments(Array.isArray(rawD) ? rawD : (Array.isArray(rawD?.data) ? rawD.data : []));
    } catch { setDepartments([]); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('start', start);
      qs.set('end', end);
      if (periodId) qs.set('periodId', String(periodId));
      if (depId) qs.set('departmentId', String(depId));
      if (authorized) qs.set('isAuthorized', authorized);
      const r: any = await api.get(`/meals/transactions?${qs.toString()}`);
      const raw = r?.data ?? r;
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setRows(arr);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Geçişler yüklenemedi');
    } finally { setLoading(false); }
  }, [start, end, periodId, depId, authorized]);

  useEffect(() => { loadMeta(); load(); }, [loadMeta, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    if (!q) return rows;
    return rows.filter(r =>
      (r.employeeName ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (r.employeeCode ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (r.cardNumber ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
      (r.departmentName ?? '').toLocaleLowerCase('tr-TR').includes(q)
    );
  }, [rows, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    authorized: rows.filter(r => r.isAuthorized).length,
    unauthorized: rows.filter(r => r.isAuthorized === false).length,
  }), [rows]);

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Building2 size={26} className="text-amber-600" /> Yemek Hareketleri
          </h1>
          <p className="text-sm text-slate-500 mt-1">Yemekhane cihazlarından yapılan geçişler</p>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Yenile
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Başlangıç</label>
          <input type="date" value={start} onChange={e => setStart(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Bitiş</label>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Öğün</label>
          <select value={periodId} onChange={e => setPeriodId(e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
            <option value="">Tümü</option>
            {periods.map(p => <option key={p.periodID} value={p.periodID}>{p.periodName}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Departman</label>
          <select value={depId} onChange={e => setDepId(e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
            <option value="">Tümü</option>
            {departments.map(d => <option key={d.departmentId ?? d.id} value={d.departmentId ?? d.id}>{d.departmentName ?? d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Yetki</label>
          <select value={authorized} onChange={e => setAuthorized(e.target.value as any)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
            <option value="">Tümü</option>
            <option value="true">Yetkili</option>
            <option value="false">İzinsiz</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Arama</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Ad, kod, kart..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
        </div>
        <div className="md:col-span-6">
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Uygula
          </button>
        </div>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-slate-900 text-white rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-300">TOPLAM</p>
          <p className="text-3xl font-black mt-1">{stats.total}</p>
        </div>
        <div className="bg-white border border-slate-200 border-l-4 border-l-emerald-500 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-emerald-700 flex items-center gap-1"><CheckCircle2 size={12} /> YETKİLİ</p>
          <p className="text-3xl font-black text-slate-800 mt-1">{stats.authorized}</p>
        </div>
        <div className="bg-white border border-slate-200 border-l-4 border-l-red-500 rounded-xl p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-red-700 flex items-center gap-1"><XCircle size={12} /> İZİNSİZ</p>
          <p className="text-3xl font-black text-slate-800 mt-1">{stats.unauthorized}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Tarih</th>
                <th className="px-4 py-3">Saat</th>
                <th className="px-4 py-3">Personel</th>
                <th className="px-4 py-3">Departman</th>
                <th className="px-4 py-3">Öğün</th>
                <th className="px-4 py-3">Cihaz</th>
                <th className="px-4 py-3">Yetki</th>
                <th className="px-4 py-3">Red Sebebi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                  <Utensils size={32} className="mx-auto opacity-30 mb-2" /> Kayıt yok
                </td></tr>
              ) : filtered.map((r, i) => (
                <tr key={r.transactionID ?? i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(r.transactionDate)}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{fmtTime(r.transactionTime)}</td>
                  <td className="px-4 py-3">
                    <p className="font-bold text-slate-800">{r.employeeName ?? '—'}</p>
                    <p className="text-[11px] text-slate-500">#{r.employeeCode ?? '—'} {r.cardNumber ? `· ${r.cardNumber}` : ''}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.departmentName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{r.periodName ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.deviceName ?? '—'}</td>
                  <td className="px-4 py-3">
                    {r.isAuthorized ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                        <CheckCircle2 size={10} /> YETKİLİ
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                        <XCircle size={10} /> İZİNSİZ
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{r.denyReason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MealTransactions;
